import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useInvalidate, useList } from '@refinedev/core';
import { Field, FormErrors, TextArea, TextInput } from '../../../components/Form.js';
import { DocumentList } from '../../../components/DocumentList.js';
import { PhotoUpload } from '../../../components/PhotoUpload.js';
import { api } from '../../../lib/api.js';

interface LeaseOption {
  id: string;
  unitLabel: string;
  propertyName: string;
  status: string;
}

interface CreatedWorkOrder {
  id: string;
}

const ACTIVE_STATUSES = new Set(['ACTIVE', 'MONTH_TO_MONTH']);

/**
 * Submit a new maintenance request. `POST /work-orders/mine` — a tenant's own
 * route, distinct from staff's `POST /api/work-orders` (see the API module's
 * header comment): no unit picker, no priority, nothing but a lease, a title,
 * and a description. Priority always comes in at MEDIUM; only staff triage
 * from there.
 *
 * Photos can't be attached before a work order id exists to attach them to,
 * so submitting the title/description doesn't leave the portal — it swaps
 * this same screen into a photo step against the row just created, reusing
 * the same upload UI MaintenanceRequestDetail offers later. "Continue"
 * (or "Skip") is what actually navigates to the request's own page.
 */
export function MaintenanceRequestForm() {
  const navigate = useNavigate();
  const invalidate = useInvalidate();

  const { data: leasesData, isLoading: leasesLoading } = useList<LeaseOption>({
    resource: 'leases',
    pagination: { mode: 'off' },
  });

  const leases = (leasesData?.data ?? []).filter((lease) => ACTIVE_STATUSES.has(lease.status));

  const [leaseId, setLeaseId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [createdWorkOrder, setCreatedWorkOrder] = useState<CreatedWorkOrder | null>(null);

  const effectiveLeaseId = leases.length === 1 ? leases[0]!.id : leaseId;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!effectiveLeaseId || !title.trim()) return;

    setSubmitting(true);
    setError(null);
    try {
      const workOrder = await api<CreatedWorkOrder>('/work-orders/mine', {
        method: 'POST',
        body: JSON.stringify({
          leaseId: effectiveLeaseId,
          title,
          description: description || null,
        }),
      });
      setCreatedWorkOrder(workOrder);
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  if (createdWorkOrder) {
    return (
      <section>
        <div className="row">
          <h1>Add photos</h1>
          <span className="shell__spacer" />
        </div>

        <div className="stack card">
          <p className="muted">
            Request submitted. Photos help whoever comes out see the issue before they arrive — add
            one now, or skip and go straight to your request.
          </p>

          <DocumentList
            filter={{ workOrderId: createdWorkOrder.id }}
            emptyMessage="No photos attached yet."
          />

          <PhotoUpload
            parent={{ workOrderId: createdWorkOrder.id }}
            defaultDocumentType="INSPECTION_PHOTO"
            simple
            imageOnly
            onUploaded={() => {
              void invalidate({ resource: 'documents', invalidates: ['list'] });
            }}
          />

          <div className="row">
            <button
              type="button"
              className="button"
              onClick={() => void navigate(`/portal/tenant/maintenance/${createdWorkOrder.id}`)}
            >
              Continue
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="row">
        <h1>New maintenance request</h1>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/portal/tenant/maintenance')}
        >
          ← All requests
        </button>
      </div>

      <form className="stack card" onSubmit={(event) => void submit(event)}>
        <FormErrors error={error} />

        {leasesLoading ? (
          <p className="muted">Loading your lease…</p>
        ) : leases.length === 0 ? (
          <p className="alert alert--error">
            We couldn't find an active lease on your account. Please contact the management office.
          </p>
        ) : leases.length > 1 ? (
          <Field label="Unit" name="leaseId" required>
            <select
              id="leaseId"
              name="leaseId"
              className="field__input"
              value={leaseId}
              onChange={(event) => setLeaseId(event.target.value)}
              required
            >
              <option value="">Select a unit</option>
              {leases.map((lease) => (
                <option key={lease.id} value={lease.id}>
                  {lease.unitLabel}, {lease.propertyName}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <p className="muted">
            {leases[0]!.unitLabel}, {leases[0]!.propertyName}
          </p>
        )}

        <Field label="What's the issue?" name="title" required>
          <TextInput
            name="title"
            value={title}
            onChange={setTitle}
            placeholder="Leaking kitchen faucet"
            required
          />
        </Field>

        <Field
          label="Details"
          name="description"
          hint="Where it is, when it started, anything that helps whoever comes out."
        >
          <TextArea name="description" value={description} onChange={setDescription} />
        </Field>

        <div className="row">
          <button
            type="submit"
            className="button"
            disabled={submitting || !effectiveLeaseId || !title.trim()}
          >
            {submitting ? 'Submitting…' : 'Submit request'}
          </button>
          <button
            type="button"
            className="button button--quiet"
            onClick={() => void navigate('/portal/tenant/maintenance')}
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}
